import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Upload, message as antMessage } from 'antd';
import { InboxOutlined, SendOutlined, DownloadOutlined } from '@ant-design/icons';
import './CompassAgentChat.css';

// CHA-352 — Compass data agent: a friendly, watchable, steerable setup wizard
// that walks a CS person from raw data → first marketing-mix model. Streams the
// backend agent (views/compass_agent.py) over SSE and renders quick-action
// buttons so CS clicks rather than reads. Styled to the Compass Portal theme.

const API = process.env.REACT_APP_API_URL || '';
const STAGES = [
    { key: 'data', label: 'Data' },
    { key: 'clean', label: 'Clean' },
    { key: 'map', label: 'Map' },
    { key: 'table', label: 'Model table' },
    { key: 'model', label: 'First model' },
];

// ── tiny, safe markdown renderer (bold/italic/code/headings/lists/tables) ──
function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function inline(s) {
    return esc(s)
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/(^|[^*])\*(?!\s)(.+?)\*/g, '$1<em>$2</em>')
        .replace(/`(.+?)`/g, '<code>$1</code>');
}
function renderMarkdown(text) {
    const lines = String(text || '').split('\n');
    const out = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        // table: header | --- | rows
        if (/\|/.test(line) && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1])) {
            const cells = (r) => r.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((c) => c.trim());
            const head = cells(line);
            i += 2;
            const rows = [];
            while (i < lines.length && /\|/.test(lines[i])) { rows.push(cells(lines[i])); i += 1; }
            const num = (v) => /^[£$€]?-?[\d.,%]+$/.test(v);
            out.push(
                '<div class="cag__table-scroll"><table><thead><tr>' +
                head.map((h) => `<th>${inline(h)}</th>`).join('') +
                '</tr></thead><tbody>' +
                rows.map((r) => '<tr>' + r.map((c) => `<td class="${num(c) ? 'cag__num' : ''}">${inline(c)}</td>`).join('') + '</tr>').join('') +
                '</tbody></table></div>'
            );
            continue;
        }
        if (/^#{1,3}\s/.test(line)) { out.push(`<h4>${inline(line.replace(/^#{1,3}\s/, ''))}</h4>`); i += 1; continue; }
        if (/^\s*[-*]\s/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) { items.push(`<li>${inline(lines[i].replace(/^\s*[-*]\s/, ''))}</li>`); i += 1; }
            out.push(`<ul>${items.join('')}</ul>`); continue;
        }
        if (/^\s*\d+\.\s/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) { items.push(`<li>${inline(lines[i].replace(/^\s*\d+\.\s/, ''))}</li>`); i += 1; }
            out.push(`<ol>${items.join('')}</ol>`); continue;
        }
        if (line.trim() === '') { i += 1; continue; }
        out.push(`<p>${inline(line)}</p>`); i += 1;
    }
    return out.join('');
}

const TOOL_LABEL = {
    inspect_file: 'Looking at your file…',
    clean_file: 'Tidying it up…',
    flag_missing: 'Checking for gaps…',
    map_columns: 'Working out your channels…',
    assemble_model_table: 'Building your model table…',
    fit_first_model: 'Fitting your first model…',
    list_sources: 'Gathering your files…',
    propose_actions: '',
};

export default function CompassAgentChat({ projectId }) {
    const token = useSelector((s) => s.oauth?.auth0Token);
    const [sessionId] = useState(() => `${projectId || 'p'}-${Date.now().toString(36)}`);
    const [items, setItems] = useState([]); // {kind:'agent'|'user'|'step'|'actions'|'results', ...}
    const [files, setFiles] = useState([]);
    const [stageIdx, setStageIdx] = useState(-1);
    const [busy, setBusy] = useState(false);
    const [downloads, setDownloads] = useState([]);
    const [draft, setDraft] = useState('');
    const [started, setStarted] = useState(false);
    const transcriptRef = useRef(null);
    // Guards so dropping several files at once uploads them ALL, then greets ONCE.
    const startedRef = useRef(false);
    const batchRef = useRef(null);

    const push = useCallback((it) => {
        setItems((prev) => [...prev, it]);
        requestAnimationFrame(() => {
            const el = transcriptRef.current;
            if (el) el.scrollTop = el.scrollHeight;
        });
    }, []);

    const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);

    // ── stream one agent turn over SSE ──
    const runTurn = useCallback(async (userMessage) => {
        setBusy(true);
        // clear any pending action buttons (a choice was made)
        setItems((prev) => prev.map((it) => (it.kind === 'actions' ? { ...it, spent: true } : it)));
        try {
            const res = await fetch(`${API}/compass-agent/message`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...authHeaders },
                body: JSON.stringify({ session: sessionId, message: userMessage }),
            });
            if (!res.ok || !res.body) throw new Error(`Server responded ${res.status}`);
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buf = '';
            for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                buf += decoder.decode(value, { stream: true });
                const chunks = buf.split('\n\n');
                buf = chunks.pop();
                for (const chunk of chunks) {
                    const dataLine = chunk.split('\n').find((l) => l.startsWith('data:'));
                    if (!dataLine) continue;
                    let ev;
                    try { ev = JSON.parse(dataLine.slice(5).trim()); } catch { continue; }
                    handleEvent(ev);
                }
            }
        } catch (e) {
            push({ kind: 'step', err: true, text: `Something went wrong: ${e.message}` });
            push({ kind: 'actions', question: '', spent: false, options: [{ id: 'retry', label: 'Try again', recommended: true, kind: 'primary' }] });
        } finally {
            setBusy(false);
        }
    }, [API, authHeaders, sessionId, push]); // eslint-disable-line

    const handleEvent = useCallback((ev) => {
        switch (ev.type) {
            case 'agent_text':
                if (ev.text && ev.text.trim()) push({ kind: 'agent', html: renderMarkdown(ev.text) });
                break;
            case 'tool_call': {
                const label = TOOL_LABEL[ev.name] ?? `${ev.name}…`;
                if (label) push({ kind: 'step', text: label });
                break;
            }
            case 'tool_result':
                break; // results are narrated by the agent; step line already shown
            case 'stage': {
                const idx = STAGES.findIndex((s) => s.key === ev.stage);
                if (idx >= 0) setStageIdx(idx);
                break;
            }
            case 'actions':
                push({ kind: 'actions', question: ev.question, options: ev.options || [], spent: false });
                break;
            case 'cleaned_ready':
                setDownloads((prev) => [...prev, { name: ev.name, label: 'Download cleaned data' }]);
                break;
            case 'model_ready':
                setStageIdx(STAGES.length - 1);
                push({ kind: 'results', results: ev });
                if (ev.results_file) setDownloads((prev) => [...prev, { name: ev.results_file, label: 'Download model results' }]);
                break;
            case 'error':
                push({ kind: 'step', err: true, text: ev.message || 'Something went wrong.' });
                break;
            default:
                break;
        }
    }, [push]);

    const onAction = useCallback((opt) => {
        push({ kind: 'user', text: opt.label });
        runTurn(opt.label);
    }, [push, runTurn]);

    const onSend = useCallback(() => {
        const t = draft.trim();
        if (!t || busy) return;
        setDraft('');
        push({ kind: 'user', text: t });
        runTurn(t);
    }, [draft, busy, push, runTurn]);

    // Download must carry the bearer token — a plain <a href> can't, so the
    // auth-gated endpoint 401s. Fetch with the header, then save the blob.
    const downloadFile = useCallback(async (name) => {
        try {
            const url = `${API}/compass-agent/download?session=${encodeURIComponent(sessionId)}&name=${encodeURIComponent(name)}`;
            const res = await fetch(url, { headers: authHeaders });
            if (!res.ok) throw new Error(`Server responded ${res.status}`);
            const blob = await res.blob();
            const obj = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = obj; a.download = name;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(obj);
        } catch (e) {
            antMessage.warning(`Couldn't download that file — ${e.message}`);
        }
    }, [API, authHeaders, sessionId]);

    // ── upload ONE file into the session (no wizard kickoff) ──
    const uploadOne = useCallback(async (file) => {
        const form = new FormData();
        form.append('session', sessionId);
        form.append('file', file);
        const res = await fetch(`${API}/compass-agent/upload`, { method: 'POST', headers: authHeaders, body: form });
        if (!res.ok) throw new Error(`Upload failed (${res.status})`);
        const data = await res.json();
        const name = data.filename || file.name;
        setFiles((prev) => [...prev, name]);
        if (startedRef.current) push({ kind: 'step', text: `Added ${name}.` });
        return name;
    }, [API, authHeaders, sessionId, push]);

    // ── AntD fires beforeUpload once PER FILE with the SAME fileList reference.
    // Handle the whole drop once (dedupe by that reference), upload every file
    // SEQUENTIALLY so nothing races on the server, THEN greet once with all files
    // present — no timing guesswork, so every file in the batch is recognised. ──
    const doUpload = useCallback((file, fileList) => {
        const batch = fileList && fileList.length ? Array.from(fileList) : [file];
        if (batchRef.current === fileList) return false; // already handling this drop
        batchRef.current = fileList;
        (async () => {
            setBusy(true);
            for (const f of batch) {
                try { await uploadOne(f); }
                catch (e) { antMessage.warning(`${f.name} didn't upload — ${e.message}`); }
            }
            if (!startedRef.current) {
                startedRef.current = true;
                setStarted(true);
                setStageIdx(0);
                await runTurn('__START__');
            }
            setBusy(false);
        })();
        return false; // prevent AntD's own upload
    }, [uploadOne, runTurn]);

    return (
        <div className="cag">
            <p className="cag__intro">
                Upload your marketing data and I'll walk you through getting it ready and building
                your first model — I'll do the work, you just check and click.
            </p>
            {!started && <span className="cag__timecue">⏱ About 2 minutes to your first model</span>}

            {/* stage progress */}
            <div className="cag__stages">
                {STAGES.map((s, i) => (
                    <div
                        key={s.key}
                        className={`cag__stage ${i < stageIdx ? 'cag__stage--done' : ''} ${i === stageIdx ? 'cag__stage--active' : ''}`}
                    >
                        <span className="cag__stage-num">{i + 1}</span>
                        {s.label}
                    </div>
                ))}
            </div>

            {files.length > 0 && (
                <div className="cag__files">
                    {files.map((f, i) => <span key={i} className="cag__file-pill">{f}</span>)}
                </div>
            )}

            {/* uploader (always available to add more) */}
            <div className="cag__uploader">
                <Upload.Dragger multiple showUploadList={false} beforeUpload={doUpload} disabled={busy}>
                    <p><InboxOutlined /></p>
                    <p>Drop a CSV here, or click to choose</p>
                    <p className="cag__hint">Spend by channel, and your sales/revenue file.</p>
                </Upload.Dragger>
            </div>

            {/* transcript */}
            <div className="cag__transcript" ref={transcriptRef}>
                {items.map((it, idx) => {
                    if (it.kind === 'agent') {
                        return <div key={idx} className="cag__msg cag__msg--agent"><div className="cag__bubble cag__md" dangerouslySetInnerHTML={{ __html: it.html }} /></div>;
                    }
                    if (it.kind === 'user') {
                        return <div key={idx} className="cag__msg cag__msg--user"><div className="cag__bubble">{it.text}</div></div>;
                    }
                    if (it.kind === 'step') {
                        return <div key={idx} className={`cag__step ${it.err ? 'cag__step--err' : ''}`}><span className="cag__step-dot" />{it.text}</div>;
                    }
                    if (it.kind === 'actions') {
                        return (
                            <div key={idx} className={`cag__actions ${it.spent ? 'cag__actions--spent' : ''}`}>
                                {it.question && <div className="cag__actions-q">{it.question}</div>}
                                {(it.options || []).map((opt) => (
                                    <Button
                                        key={opt.id}
                                        type={opt.recommended || opt.kind === 'primary' ? 'primary' : 'default'}
                                        disabled={it.spent || busy}
                                        onClick={() => onAction(opt)}
                                    >
                                        {opt.label}
                                        {opt.recommended && <span className="cag__rec-tag">✓ Recommended</span>}
                                    </Button>
                                ))}
                            </div>
                        );
                    }
                    if (it.kind === 'results') {
                        return <ResultsPanel key={idx} r={it.results} />;
                    }
                    return null;
                })}
                {busy && <div className="cag__typing"><span /><span /><span /></div>}
            </div>

            {downloads.length > 0 && (
                <div className="cag__downloads">
                    {downloads.map((d, i) => (
                        <Button key={i} icon={<DownloadOutlined />} onClick={() => downloadFile(d.name)}>
                            {d.label}
                        </Button>
                    ))}
                </div>
            )}

            {started && (
                <div className="cag__composer">
                    <input
                        value={draft}
                        placeholder="Ask a question or tell me what to change…"
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') onSend(); }}
                        disabled={busy}
                        aria-label="Message the data agent"
                    />
                    <Button type="primary" icon={<SendOutlined />} onClick={onSend} disabled={busy || !draft.trim()} />
                </div>
            )}
        </div>
    );
}

function ResultsPanel({ r }) {
    const channels = (r.channels || []).map((c) => ({
        name: c.name || c.channel || 'channel',
        pct: c.contribution_pct || 0,
        roi: c.roi,
    }));
    const max = Math.max(1, ...channels.map((c) => c.pct), r.base_pct || 0);
    return (
        <div className="cag__results">
            <div className="cag__results-fit">Model fit: {Math.round((r.r_squared || 0) * 100)}%</div>
            <div className="cag__bar-row cag__bar-row--base">
                <span className="cag__bar-name">Baseline</span>
                <span className="cag__bar-track"><span className="cag__bar-fill" style={{ width: `${((r.base_pct || 0) / max) * 100}%` }} /></span>
                <span className="cag__bar-val">{Math.round(r.base_pct || 0)}%</span>
            </div>
            {channels.map((c) => (
                <div key={c.name} className="cag__bar-row">
                    <span className="cag__bar-name">{c.name.replace(/_spend_gbp$/, '').replace(/_/g, ' ')}</span>
                    <span className="cag__bar-track"><span className="cag__bar-fill" style={{ width: `${(c.pct / max) * 100}%` }} /></span>
                    <span className="cag__bar-val">{Math.round(c.pct)}%{c.roi != null ? ` · £${Number(c.roi).toFixed(2)}` : ''}</span>
                </div>
            ))}
            {(r.findings || []).length > 0 && (
                <ul className="cag__findings">{r.findings.map((f, i) => <li key={i}>{f}</li>)}</ul>
            )}
        </div>
    );
}
